import React, { useState, useEffect } from 'react';
import { apiService, Object } from '../services/api';
import logger from '../utils/logger';

const ObjectManager: React.FC = () => {
  const [objects, setObjects] = useState<Object[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newObject, setNewObject] = useState({ name: '', description: '' });
  const [editObject, setEditObject] = useState({ name: '', description: '' });

  useEffect(() => {
    loadObjects();
  }, []);

  const loadObjects = async () => {
    try {
      setLoading(true);
      const data = await apiService.getObjects();
      setObjects(data);
    } catch (err) {
      setError('Failed to load objects');
      logger.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newObject.name.trim()) return;

    try {
      await apiService.createObject({
        name: newObject.name.trim(),
        description: newObject.description.trim()
      });
      setNewObject({ name: '', description: '' });
      await loadObjects();
    } catch (err: any) {
      // Handle unique constraint violation
      if (err.response?.status === 409) {
        setError(err.response.data.details || 'An object with this name already exists');
      } else {
        setError('Failed to create object');
      }
      logger.error('Create error:', err);
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editObject.name.trim()) return;

    try {
      await apiService.updateObject(id, {
        name: editObject.name.trim(),
        description: editObject.description.trim()
      });
      setEditingId(null);
      await loadObjects();
    } catch (err: any) {
      // Handle unique constraint violation
      if (err.response?.status === 409) {
        setError(err.response.data.details || 'An object with this name already exists');
      } else {
        setError('Failed to update object');
      }
      logger.error('Update error:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this object?')) return;

    try {
      await apiService.deleteObject(id);
      await loadObjects();
    } catch (err) {
      setError('Failed to delete object');
      logger.error('Delete error:', err);
    }
  };

  const startEdit = (object: Object) => {
    setEditingId(object.id!);
    setEditObject({
      name: object.name,
      description: object.description
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditObject({ name: '', description: '' });
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null); // Clear any previous errors
      const result = await apiService.uploadObjectsCSV(file);
      
      if ((result.errors && result.errors.length > 0) || (result.duplicates && result.duplicates.length > 0)) {
        // Show partial success with errors and/or duplicates
        let message = `Upload completed:\n\nSuccessfully uploaded: ${result.processedCount} objects\nTotal processed: ${result.totalCount || result.processedCount}`;
        
        if (result.duplicates && result.duplicates.length > 0) {
          message += `\n\nDuplicates skipped (${result.duplicates.length}):\n${result.duplicates.join('\n')}`;
        }
        
        if (result.errors && result.errors.length > 0) {
          message += `\n\nErrors (${result.errors.length}):\n${result.errors.join('\n')}`;
        }
        
        alert(message);
      } else {
        alert(`Successfully uploaded ${result.processedCount} objects`);
      }
      
      await loadObjects();
    } catch (err: any) {
      logger.error('Upload error:', err);
      
      // Try to extract meaningful error message from the response
      let errorMessage = 'Failed to upload CSV';
      
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your API key configuration.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access forbidden. Insufficient permissions.';
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
          if (err.response.data.details && Array.isArray(err.response.data.details)) {
            errorMessage += '\nDetails:\n' + err.response.data.details.join('\n');
          }
        } else {
          errorMessage = `Server error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Network error - request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      e.target.value = '';
    }
  };


  const handleCSVDownload = async () => {
    try {
      setError(null); // Clear any previous errors
      logger.info('Starting objects CSV download...');
      
      const blob = await apiService.downloadObjectsCSV();
      logger.info('Downloaded blob:', {
        size: blob.size,
        type: blob.type
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'objects.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      logger.info('Objects CSV download completed successfully');
    } catch (err: any) {
      logger.error('Download error:', err);
      
      // Try to extract meaningful error message from the response
      let errorMessage = 'Failed to download CSV';
      
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your API key configuration.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access forbidden. Insufficient permissions.';
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
          if (err.response.data.details) {
            errorMessage += '\nDetails: ' + err.response.data.details;
          }
        } else {
          errorMessage = `Server error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Network error - request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  if (loading) return <div className="p-4">Loading objects...</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Object Management</h2>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="flex">
              <div className="ml-3 w-full">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700 whitespace-pre-line">{error}</div>
                <button
                  onClick={() => setError(null)}
                  className="mt-2 text-sm text-red-600 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSV Upload/Download Section */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-3">CSV Operations</h3>
          <div className="flex gap-4 items-center">
            <div>
              <label htmlFor="csv-upload" className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors">
                Upload CSV
              </label>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="hidden"
              />
            </div>
            <button
              onClick={handleCSVDownload}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
            >
              Download CSV
            </button>
            <span className="text-sm text-gray-600">
              CSV format: name,description (header row required). Description is optional. Use quotes for values containing commas.
            </span>
          </div>
        </div>

        {/* Create New Object Form */}
        <form onSubmit={handleCreate} className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-3">Add New Object</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={newObject.name}
                onChange={(e) => setNewObject(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Object name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newObject.description}
                onChange={(e) => setNewObject(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Object description (optional)"
              />
            </div>
          </div>
          <button
            type="submit"
            className="mt-4 bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors"
          >
            Add Object
          </button>
        </form>
      </div>

      {/* Objects List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">All Objects ({objects.length})</h3>
        </div>
        
        {objects.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No objects found. Create your first object above.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {objects.map((object) => (
              <div key={object.id} className="p-4 hover:bg-gray-50">
                {editingId === object.id ? (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={editObject.name}
                          onChange={(e) => setEditObject(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={editObject.description}
                          onChange={(e) => setEditObject(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdate(object.id!)}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          ID: {object.id}
                        </span>
                        <h4 className="font-semibold text-gray-900">
                          {object.name}
                        </h4>
                      </div>
                      <p className="text-gray-600 mb-2">{object.description}</p>
                      <div className="text-xs text-gray-500">
                        Created: {object.created_at ? new Date(object.created_at).toLocaleString() : 'Unknown'}
                        {object.updated_at && object.updated_at !== object.created_at && (
                          <span className="ml-4">
                            Updated: {new Date(object.updated_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => startEdit(object)}
                        className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition-colors text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(object.id!)}
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectManager;